import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuotes, downloadQuotePdf, openQuotePdfForPrint, useSendQuoteEmail } from '@/hooks/useApi';
import Tooltip from '@/components/Tooltip';
import PageSkeleton from '@/components/PageSkeleton';
import Pagination from '@/components/Pagination';
import ActivityModal from '@/components/ActivityModal';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/* ── SVG icon components ── */
const DownloadIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PrintIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);


const EnvelopeIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-gray', sent: 'badge-blue', viewed: 'badge-blue',
  accepted: 'badge-green', rejected: 'badge-red', expired: 'badge-gray', converted: 'badge-green',
};

const ROW_COLORS: Record<string, string> = {
  draft: 'row-gray',
  sent: 'row-blue',
  viewed: 'row-sky',
  accepted: 'row-green',
  rejected: 'row-red',
  expired: 'row-gray',
  converted: 'row-emerald',
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

const SendIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export default function QuotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const statusFilter = searchParams.get('status') || '';
  const page = Number(searchParams.get('page')) || 1;
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Use debounced search from URL params for the API call
  const debouncedSearch = searchParams.get('search') || '';
  const { data, isLoading, isFetching } = useQuotes({ page, status: statusFilter, search: debouncedSearch });
  const sendQuoteEmail = useSendQuoteEmail();
  const [sendModalQuote, setSendModalQuote] = useState<any>(null);
  const [sendLanguage, setSendLanguage] = useState('');
  const [activityQuote, setActivityQuote] = useState<{ id: string; number: string } | null>(null);

  function setFilter(key: string, value: string) {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value);
    else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  }

  function setPage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  }

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter('search', value);
    }, 300);
  }, [searchParams]);

  if (isLoading && !data) return <PageSkeleton />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold whitespace-nowrap">{t('quotes.title')}</h1>
        <input
          type="text"
          placeholder={t('common.search') + '...'}
          className="input flex-1 min-w-[200px]"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <select className="input w-auto" value={statusFilter} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">{t('common.allStatuses')}</option>
          {['draft', 'sent', 'viewed', 'accepted', 'rejected', 'expired', 'converted'].map((s) => (
            <option key={s} value={s}>{t(`quotes.status.${s}`)}</option>
          ))}
        </select>
        <Link to="/quotes/new" className="btn btn-primary whitespace-nowrap">
          + {t('quotes.createQuote')}
        </Link>
      </div>

      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('quotes.number')}</th>
              <th>{t('quotes.client')}</th>
              <th>{t('quotes.date')}</th>
              <th>{t('quotes.validUntil')}</th>
              <th className="text-right">{t('common.amount')}</th>
              <th>{t('common.status')}</th>
              <th className="text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isFetching && !data?.data?.length && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="skeleton h-4 w-20" /></td>)}
              </tr>
            ))}
            {data?.data?.map((q) => (
              <tr key={q.id} className={`cursor-pointer ${ROW_COLORS[q.status] || ''}`} onClick={() => navigate(`/quotes/${q.id}`)}>
                <td>
                  <Link to={`/quotes/${q.id}`} className="text-primary-600 font-medium hover:underline">
                    {q.quoteNumber}
                  </Link>
                  {q.emailSentAt && (
                    <Tooltip text={t('quotes.emailSent')}>
                      <EnvelopeIcon className="h-3.5 w-3.5 text-emerald-500 ml-1 inline" />
                    </Tooltip>
                  )}
                </td>
                <td className="text-gray-600">{q.client?.name}</td>
                <td className="text-gray-500">{new Date(q.issueDate).toLocaleDateString()}</td>
                <td className="text-gray-500">{q.validUntil ? new Date(q.validUntil).toLocaleDateString() : '—'}</td>
                <td className="text-right font-medium">€{Number(q.totalAmount).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</td>
                <td><span className={`badge ${STATUS_COLORS[q.status] || 'badge-gray'}`}>{t(`quotes.status.${q.status}`)}</span></td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5">
                    <div className="flex items-center gap-0.5 border border-gray-200 rounded-md px-1 py-0.5 bg-white">
                      <Tooltip text={`${t('quotes.actions.download')} — ${q.quoteNumber}`}>
                        <button
                          className="p-1 rounded hover:bg-purple-50 text-purple-600 transition-colors"
                          onClick={() => downloadQuotePdf(q.id, `${q.quoteNumber}.pdf`).catch(() => toast.error(t('common.error')))}
                        >
                          <DownloadIcon />
                        </button>
                      </Tooltip>
                      <Tooltip text={`${t('quotes.actions.print')} — ${q.quoteNumber}`}>
                        <button
                          className="p-1 rounded hover:bg-purple-50 text-purple-600 transition-colors"
                          onClick={() => openQuotePdfForPrint(q.id).catch(() => toast.error(t('common.error')))}
                        >
                          <PrintIcon />
                        </button>
                      </Tooltip>
                      {(q.status === 'draft' || q.status === 'sent') && (
                        <Tooltip text={t('quotes.actions.sendToClient')}>
                          <button
                            className="p-1 rounded hover:bg-emerald-50 text-emerald-600 transition-colors"
                            onClick={() => {
                              setSendLanguage(q.client?.language || 'en');
                              setSendModalQuote(q);
                            }}
                          >
                            <SendIcon />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip text={t('activity.viewActivity')}>
                        <button
                          className="p-1 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                          onClick={() => setActivityQuote({ id: q.id, number: q.quoteNumber })}
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {!isFetching && (!data?.data || data.data.length === 0) && (
              <tr><td colSpan={7} className="text-center text-gray-400 py-12">{t('common.noResults')}</td></tr>
            )}
          </tbody>
        </table>
        <Pagination meta={data?.meta} onPageChange={setPage} />
      </div>

      {/* Send email modal */}
      {sendModalQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 min-w-[340px] max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">{t('quotes.sendQuoteTitle')}</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('quotes.emailLanguage')}</label>
            <select className="input w-full mb-4" value={sendLanguage} onChange={(e) => setSendLanguage(e.target.value)}>
              {LANGUAGES.map((l) => (<option key={l.code} value={l.code}>{l.label}</option>))}
            </select>
            <div className="flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={() => setSendModalQuote(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" disabled={sendQuoteEmail.isPending} onClick={() => {
                sendQuoteEmail.mutate(
                  { id: sendModalQuote.id, language: sendLanguage },
                  {
                    onSuccess: () => setSendModalQuote(null),
                  }
                );
              }}>
                {sendQuoteEmail.isPending ? t('quotes.sending') : t('quotes.actions.sendToClient')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityQuote && (
        <ActivityModal
          mode="entity"
          entityType="Quote"
          entityId={activityQuote.id}
          isOpen={true}
          onClose={() => setActivityQuote(null)}
          title={`${t('activity.title')} — ${activityQuote.number}`}
        />
      )}
    </div>
  );
}
